import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as JSZip from 'jszip';
import { Subscription } from 'rxjs';
import { ConversationHistoryService } from '../../../core/services/conversation-history.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  TranslationKeys,
  TranslationService,
} from '../../../core/services/translation.service';
import { TranslatorService } from '../../../core/services/translator.service';
import { ConversationHistoryEntry } from '../../../shared/models';

@Component({
  selector: 'app-conversation-history-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './conversation-history-modal.component.html',
  styleUrls: ['./conversation-history-modal.component.css'],
})
export class ConversationHistoryModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() playAudio = new EventEmitter<ConversationHistoryEntry>();
  @Output() clearMainConversations = new EventEmitter<void>();

  conversations: ConversationHistoryEntry[] = [];
  filteredConversations: ConversationHistoryEntry[] = [];
  searchQuery = '';
  isLoading = false;

  // Translations
  translations: TranslationKeys = {} as TranslationKeys;
  private translationSubscription?: Subscription;

  // Make Math available in template
  Math = Math;

  // Cache for blob URLs to prevent ExpressionChangedAfterItHasBeenCheckedError
  private blobUrlCache = new Map<string, string>();

  // Track which entries are currently generating audio
  private generatingAudioSet = new Set<string>();
  private generatingOriginalAudioSet = new Set<string>();

  private subscription?: Subscription;

  constructor(
    private conversationHistoryService: ConversationHistoryService,
    private toastService: ToastService,
    private translationService: TranslationService,
    private translatorService: TranslatorService
  ) {}

  ngOnInit(): void {
    console.log('Modal ngOnInit called, isOpen:', this.isOpen);
    this.subscription = this.conversationHistoryService.history$.subscribe(
      (conversations) => {
        console.log('Modal received conversations:', conversations.length);
        this.conversations = conversations;
        this.filterConversations();
      }
    );

    // Subscribe to translations
    this.translations = this.translationService.getAllTranslations();
    this.translationSubscription = this.translationService.currentLanguage$.subscribe(() => {
      this.translations = this.translationService.getAllTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Modal ngOnChanges called, changes:', changes);
    console.log('Modal current isOpen value:', this.isOpen);
    if (changes['isOpen']) {
      console.log(
        'Modal isOpen changed from',
        changes['isOpen'].previousValue,
        'to',
        changes['isOpen'].currentValue
      );
      console.log('Modal isOpen changed to:', this.isOpen);
      if (this.isOpen) {
        console.log('Modal opened, conversations:', this.conversations.length);
        this.preventBodyScroll();
      } else {
        this.restoreBodyScroll();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
    // Ensure body scroll is restored when component is destroyed
    this.restoreBodyScroll();

    // Clean up blob URLs to prevent memory leaks
    this.blobUrlCache.forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    this.blobUrlCache.clear();
  }

  onSearchChange(): void {
    this.filterConversations();
  }

  private filterConversations(): void {
    let allConversations: ConversationHistoryEntry[];

    if (this.searchQuery.trim()) {
      allConversations = this.conversationHistoryService.searchConversations(
        this.searchQuery
      );
    } else {
      allConversations =
        this.conversationHistoryService.getConversationsSortedByTime();
    }

    // Show all conversations regardless of language pairs
    // This allows users to see all their translation history
    this.filteredConversations = allConversations;
  }

  onClose(): void {
    console.log('Modal onClose() called');
    this.restoreBodyScroll();
    this.close.emit();
  }

  onPlayAudio(conversation: ConversationHistoryEntry): void {
    this.playAudio.emit(conversation);
  }

  onPlayPartnerAudio(conversation: ConversationHistoryEntry): void {
    // Create a modified conversation entry with only partner recorded audio
    const partnerConversation = {
      ...conversation,
      audioUrl: conversation.recordedAudioUrl,
      audioData: conversation.recordedAudioData,
      recordedAudioUrl: undefined,
      recordedAudioData: undefined,
    };
    this.playAudio.emit(partnerConversation);
  }

  onPlayTranslatedAudio(conversation: ConversationHistoryEntry): void {
    // Create a modified conversation entry with only translated audio
    const translatedConversation = {
      ...conversation,
      recordedAudioUrl: undefined,
      recordedAudioData: undefined,
    };
    this.playAudio.emit(translatedConversation);
  }

  getOriginalAudioUrl(conversation: ConversationHistoryEntry): string {
    if (conversation.originalAudioUrl) {
      return conversation.originalAudioUrl;
    }
    if (conversation.originalAudioData) {
      return this.convertBase64ToBlobUrl(conversation.originalAudioData);
    }
    return '';
  }

  getPartnerAudioUrl(conversation: ConversationHistoryEntry): string {
    console.log(`🎙️ [GET PARTNER AUDIO URL] Conversation ${conversation.id}:`, {
      hasRecordedAudioUrl: !!conversation.recordedAudioUrl,
      hasRecordedAudioData: !!conversation.recordedAudioData,
      recordedAudioDataLength: conversation.recordedAudioData?.length || 0,
    });

    if (conversation.recordedAudioUrl) {
      console.log(
        `🎙️ Returning recordedAudioUrl: ${conversation.recordedAudioUrl.substring(
          0,
          100
        )}`
      );
      return conversation.recordedAudioUrl;
    }
    if (conversation.recordedAudioData) {
      const blobUrl = this.convertBase64ToBlobUrl(
        conversation.recordedAudioData
      );
      console.log(
        `🎙️ Converted recordedAudioData to blob URL: ${blobUrl.substring(
          0,
          100
        )}`
      );
      return blobUrl;
    }
    console.log(`🎙️ No recorded audio found, returning empty string`);
    return '';
  }

  getTranslatedAudioUrl(conversation: ConversationHistoryEntry): string {
    if (conversation.audioUrl) {
      return conversation.audioUrl;
    }
    if (conversation.audioData) {
      return this.convertBase64ToBlobUrl(conversation.audioData);
    }
    return '';
  }

  // Get primary audio URL with priority: original > translated
  getPrimaryAudioUrl(conversation: ConversationHistoryEntry): string {
    // Priority 1: Original audio (from text translation mode)
    if (conversation.originalAudioUrl || conversation.originalAudioData) {
      return this.getOriginalAudioUrl(conversation);
    }

    // Priority 2: Translated audio
    if (conversation.audioUrl || conversation.audioData) {
      return this.getTranslatedAudioUrl(conversation);
    }

    return '';
  }

  // Helper function to safely convert base64 data to blob URL with caching
  private convertBase64ToBlobUrl(base64Data: string): string {
    try {
      // Check cache first
      if (this.blobUrlCache.has(base64Data)) {
        return this.blobUrlCache.get(base64Data)!;
      }

      // Validate base64 data
      if (!base64Data || typeof base64Data !== 'string') {
        console.warn('🎙️ Invalid base64 data provided:', typeof base64Data);
        return '';
      }

      let base64String = base64Data;

      // Handle data URLs (e.g., "data:audio/mpeg;base64,//PExAB...")
      if (base64String.includes(',')) {
        base64String = base64String.split(',')[1];
      }

      // Validate base64 string format
      if (!base64String || base64String.length === 0) {
        console.warn('🎙️ Empty base64 string after processing');
        return '';
      }

      // Check if the string contains valid base64 characters
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(base64String)) {
        console.warn(
          '🎙️ Invalid base64 format:',
          base64String.substring(0, 50) + '...'
        );
        return '';
      }

      // Decode base64 string
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Create blob and URL
      const blob = new Blob([byteArray], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(blob);

      // Cache the URL
      this.blobUrlCache.set(base64Data, blobUrl);

      console.log('🎙️ Successfully converted base64 to blob URL');
      return blobUrl;
    } catch (error) {
      console.error('🎙️ Error converting base64 to blob URL:', error);
      console.error(
        '🎙️ Base64 data preview:',
        base64Data?.substring(0, 100) + '...'
      );
      return '';
    }
  }

  onDeleteConversation(conversation: ConversationHistoryEntry): void {
    if (confirm('Are you sure you want to delete this conversation?')) {
      this.conversationHistoryService.deleteConversation(conversation.id);
    }
  }

  // Simple download method for mobile using Capacitor FileSystem
  async downloadAudio(audioUrl: string, filename: string): Promise<void> {
    try {
      console.log('📥 Download requested:', filename);

      // Check if it's a mobile device
      const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

      if (!isMobile) {
        // Desktop: Use standard download
        const link = document.createElement('a');
        link.href = audioUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('✅ Desktop download initiated');
        return;
      }

      // Mobile: Use Capacitor FileSystem
      const { Filesystem, Directory } = await import('@capacitor/filesystem');

      // Fetch the audio blob
      const response = await fetch(audioUrl);
      const blob = await response.blob();

      // Convert blob to base64
      const base64Data = await this.blobToBase64String(blob);

      // For iOS, try to use Share API to let user choose where to save
      if (
        isIOS &&
        navigator.share &&
        typeof navigator.canShare === 'function'
      ) {
        try {
          console.log('📱 [iOS] Attempting to share file:', filename);
          console.log('📱 [iOS] Blob size:', blob.size, 'bytes');
          console.log('📱 [iOS] Blob type:', blob.type);

          // Create a File object from the blob with proper MIME type
          const file = new File([blob], filename, {
            type: 'audio/mpeg',
            lastModified: Date.now(),
          });

          console.log('📱 [iOS] Created File object:', {
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
          });

          // Check if we can share files
          const canShareResult = navigator.canShare({ files: [file] });
          console.log('📱 [iOS] Can share result:', canShareResult);

          if (canShareResult) {
            console.log('📱 [iOS] Opening share sheet for file:', filename);

            await navigator.share({
              files: [file],
              title: 'Save Audio File',
              text: `Audio file: ${filename}`,
            });

            console.log('✅ [iOS] Share API succeeded');

            // Share successful - exit silently without alert
            return;
          } else {
            console.log(
              '⚠️ [iOS] Cannot share file, falling back to FileSystem'
            );
          }
        } catch (shareError: any) {
          console.log('🔍 [iOS SHARE ERROR] Full error details:', shareError);
          console.log('🔍 [iOS SHARE ERROR] Error name:', shareError.name);
          console.log(
            '🔍 [iOS SHARE ERROR] Error message:',
            shareError.message
          );
          console.log('🔍 [iOS SHARE ERROR] Error stack:', shareError.stack);

          // Check if user canceled the share - be more thorough
          if (
            shareError.name === 'AbortError' ||
            shareError.name === 'NotAllowedError' ||
            shareError.name === 'NotSupportedError' ||
            shareError.message?.includes('cancel') ||
            shareError.message?.includes('abort') ||
            shareError.message?.includes('user cancelled') ||
            shareError.message?.includes('user canceled') ||
            shareError.message?.includes('not supported')
          ) {
            console.log(
              'ℹ️ [iOS] User canceled or Share API not supported - exiting silently'
            );
            return; // Exit silently without showing success message
          }

          console.warn(
            '⚠️ [iOS] Share API failed, falling back to FileSystem:',
            shareError
          );
          // Fall through to FileSystem method
        }
      } else {
        console.log(
          '📱 [iOS] Share API not available, using FileSystem fallback'
        );
      }

      // Fallback: Save to Documents directory using Capacitor FileSystem
      console.log('📱 [iOS] Using FileSystem fallback for:', filename);

      try {
        // Create an 'Audio' subdirectory for better organization
        const result = await Filesystem.writeFile({
          path: `Audio/${filename}`,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true, // Create the Audio subdirectory if it doesn't exist
        });

        console.log('✅ [iOS] File saved successfully:', result.uri);
        console.log('📁 [iOS] Relative path: Documents/Audio/' + filename);

        // File saved successfully - no alert needed
      } catch (filesystemError: any) {
        console.error('❌ [iOS] FileSystem error:', filesystemError);
        // Error logged to console - no alert needed
      }
    } catch (error) {
      console.error('❌ Download failed:', error);
      // Error logged to console - no alert needed
    }
  }

  // Helper to convert blob to base64 string
  private blobToBase64String(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Check if partner audio is available (recorded audio from partner)
  hasPartnerAudio(conversation: ConversationHistoryEntry): boolean {
    const hasData = !!(
      (conversation.recordedAudioData &&
        conversation.recordedAudioData.length > 0) ||
      (conversation.recordedAudioUrl &&
        conversation.recordedAudioUrl.length > 0)
    );
    const debugInfo = {
      hasData,
      hasRecordedAudioData: !!conversation.recordedAudioData,
      hasRecordedAudioUrl: !!conversation.recordedAudioUrl,
      recordedAudioDataLength: conversation.recordedAudioData?.length || 0,
      inputType: conversation.inputType,
      originalText: conversation.originalText?.substring(0, 50) + '...',
      recordedAudioDataPreview:
        conversation.recordedAudioData?.substring(0, 100) || 'null',
      recordedAudioUrlPreview:
        conversation.recordedAudioUrl?.substring(0, 100) || 'null',
    };
    console.log(
      `🔍 [AUDIO DEBUG] Conversation ${conversation.id} - hasPartnerAudio:`,
      debugInfo
    );
    return hasData;
  }

  // Check if original audio is available (TTS of original text)
  hasOriginalAudio(conversation: ConversationHistoryEntry): boolean {
    const hasData = !!(
      (conversation.originalAudioData &&
        conversation.originalAudioData.length > 0) ||
      (conversation.originalAudioUrl &&
        conversation.originalAudioUrl.length > 0)
    );
    const debugInfo = {
      hasData,
      hasOriginalAudioData: !!conversation.originalAudioData,
      hasOriginalAudioUrl: !!conversation.originalAudioUrl,
      originalAudioDataLength: conversation.originalAudioData?.length || 0,
      inputType: conversation.inputType,
      originalText: conversation.originalText?.substring(0, 50) + '...',
      originalAudioDataPreview:
        conversation.originalAudioData?.substring(0, 100) || 'null',
      originalAudioUrlPreview:
        conversation.originalAudioUrl?.substring(0, 100) || 'null',
    };
    console.log(
      `🔍 [ORIGINAL AUDIO DEBUG] Conversation ${conversation.id} - hasOriginalAudio:`,
      debugInfo
    );
    return hasData;
  }

  // Check if translated audio is actually available and playable
  hasTranslatedAudio(conversation: ConversationHistoryEntry): boolean {
    return !!(
      (conversation.audioData && conversation.audioData.length > 0) ||
      (conversation.audioUrl && conversation.audioUrl.length > 0)
    );
  }

  // Check if any audio is available
  hasAnyAudio(conversation: ConversationHistoryEntry): boolean {
    return (
      this.hasPartnerAudio(conversation) ||
      this.hasOriginalAudio(conversation) ||
      this.hasTranslatedAudio(conversation)
    );
  }

  // Get language name for display (matching web app)
  getLanguageName(languageCode: string): string {
    const languageNames: { [key: string]: string } = {
      en: 'English',
      ko: 'Korean',
      'zh-CN': 'Mandarin',
      'zh-HK': 'Cantonese',
      zh: 'Mandarin', // Backward compatibility
      ru: 'Russian',
      ky: 'Kyrgyz',
      kk: 'Kazakh',
      tg: 'Tajik',
      tk: 'Turkmen',
      uz: 'Uzbek',
      fa: 'Farsi',
      es: 'Spanish',
    };
    return languageNames[languageCode] || languageCode || 'Unknown';
  }

  // Download all audio files from all conversations
  async onDownloadAll(): Promise<void> {
    if (this.filteredConversations.length === 0) {
      console.log('⚠️ No conversations to download');
      return;
    }

    // Count total files to download
    let totalFiles = 0;
    for (const conversation of this.filteredConversations) {
      if (
        conversation.inputType === 'speech' &&
        this.hasPartnerAudio(conversation)
      ) {
        totalFiles++;
      }
      if (
        conversation.inputType === 'text' &&
        this.hasOriginalAudio(conversation)
      ) {
        totalFiles++;
      }
      if (this.hasTranslatedAudio(conversation)) {
        totalFiles++;
      }
    }

    if (totalFiles === 0) {
      console.log('⚠️ No audio files available to download');
      return;
    }

    console.log(
      `📥 Starting download of ${totalFiles} audio file${
        totalFiles > 1 ? 's' : ''
      } from ${this.filteredConversations.length} conversation${
        this.filteredConversations.length > 1 ? 's' : ''
      }`
    );

    try {
      let downloadCount = 0;
      let errorCount = 0;

      // Use Capacitor platform detection instead of user agent
      const { Capacitor } = await import('@capacitor/core');
      const isMobile = Capacitor.isNativePlatform();
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

      console.log('🔍 [DOWNLOAD ALL] Platform detection:', {
        isNativePlatform: isMobile,
        platform: Capacitor.getPlatform(),
        userAgent: navigator.userAgent,
        isIOS,
      });

      // For iOS mobile, try Share API for bulk download with user choice
      const hasShareAPI = typeof navigator.share === 'function';
      const hasCanShare = typeof navigator.canShare === 'function';

      console.log('🔍 [DOWNLOAD ALL] Checking iOS Share API conditions:', {
        isMobile,
        isIOS,
        hasNavigatorShare: hasShareAPI,
        hasCanShare: hasCanShare,
        allConditionsMet: isMobile && isIOS && hasShareAPI && hasCanShare,
      });

      if (isMobile && isIOS && hasShareAPI && hasCanShare) {
        console.log(
          '📱 [DOWNLOAD ALL] iOS detected - using Share API for user choice'
        );

        // Collect all audio files into a single array for sharing
        const audioFiles: File[] = [];

        for (const conversation of this.filteredConversations) {
          // Collect recorded audio (speech input)
          if (
            conversation.inputType === 'speech' &&
            this.hasPartnerAudio(conversation)
          ) {
            try {
              const audioUrl = this.getPartnerAudioUrl(conversation);
              const response = await fetch(audioUrl);
              const blob = await response.blob();
              const filename = `you-spoke-${this.getLanguageName(
                conversation.userLanguage
              )}-${conversation.id}.mp3`;

              console.log(
                `📱 [iOS] Creating file for recorded audio: ${filename} (${blob.size} bytes)`
              );

              const file = new File([blob], filename, {
                type: 'audio/mpeg',
                lastModified: Date.now(),
              });
              audioFiles.push(file);
              downloadCount++;
            } catch (error) {
              console.error(
                '❌ [iOS] Error preparing recorded audio for share:',
                error
              );
              errorCount++;
            }
          }

          // Collect original audio (TTS for text input)
          if (
            conversation.inputType === 'text' &&
            this.hasOriginalAudio(conversation)
          ) {
            try {
              const audioUrl = this.getOriginalAudioUrl(conversation);
              const response = await fetch(audioUrl);
              const blob = await response.blob();
              const filename = `you-wrote-${this.getLanguageName(
                conversation.userLanguage
              )}-${conversation.id}.mp3`;

              console.log(
                `📱 [iOS] Creating file for original audio: ${filename} (${blob.size} bytes)`
              );

              const file = new File([blob], filename, {
                type: 'audio/mpeg',
                lastModified: Date.now(),
              });
              audioFiles.push(file);
              downloadCount++;
            } catch (error) {
              console.error(
                '❌ [iOS] Error preparing original audio for share:',
                error
              );
              errorCount++;
            }
          }

          // Collect translated audio (always available)
          if (this.hasTranslatedAudio(conversation)) {
            try {
              const audioUrl = this.getTranslatedAudioUrl(conversation);
              const response = await fetch(audioUrl);
              const blob = await response.blob();
              const actionType =
                conversation.inputType === 'text' ? 'wrote' : 'hears';
              const filename = `partner-${actionType}-${this.getLanguageName(
                conversation.targetLanguage
              )}-${conversation.id}.mp3`;

              console.log(
                `📱 [iOS] Creating file for translated audio: ${filename} (${blob.size} bytes)`
              );

              const file = new File([blob], filename, {
                type: 'audio/mpeg',
                lastModified: Date.now(),
              });
              audioFiles.push(file);
              downloadCount++;
            } catch (error) {
              console.error(
                '❌ [iOS] Error preparing translated audio for share:',
                error
              );
              errorCount++;
            }
          }
        }

        if (audioFiles.length > 0) {
          try {
            console.log(
              `📱 [DOWNLOAD ALL] Sharing ${audioFiles.length} files via iOS Share API`
            );
            console.log(
              '📱 [DOWNLOAD ALL] Files to share:',
              audioFiles.map((f) => ({
                name: f.name,
                size: f.size,
                type: f.type,
              }))
            );

            // iOS might have limitations on number of files or total size
            // Let's try to share in smaller batches if needed
            const maxFilesPerBatch = 10; // iOS might limit to around 10 files
            const filesToShare = audioFiles.slice(0, maxFilesPerBatch);

            if (audioFiles.length > maxFilesPerBatch) {
              console.log(
                `⚠️ [iOS] Too many files (${audioFiles.length}), sharing first ${maxFilesPerBatch} files only`
              );
              // File limit warning logged to console only
            }

            // Check if we can share all files
            const canShareResult = navigator.canShare({ files: filesToShare });
            console.log('📱 [DOWNLOAD ALL] Can share result:', canShareResult);

            if (!canShareResult) {
              throw new Error(
                'Cannot share these files - possibly too many or too large'
              );
            }

            await navigator.share({
              files: filesToShare,
              title: 'Save Audio Files',
              text: `${filesToShare.length} audio files from conversation history`,
            });

            console.log('✅ [DOWNLOAD ALL] iOS Share API succeeded');

            // Share successful - exit silently without alert
            return;
          } catch (shareError: any) {
            console.log(
              '🔍 [DOWNLOAD ALL SHARE ERROR] Full error details:',
              shareError
            );
            console.log(
              '🔍 [DOWNLOAD ALL SHARE ERROR] Error name:',
              shareError.name
            );
            console.log(
              '🔍 [DOWNLOAD ALL SHARE ERROR] Error message:',
              shareError.message
            );
            console.log(
              '🔍 [DOWNLOAD ALL SHARE ERROR] Error stack:',
              shareError.stack
            );

            // Check if user canceled the share - be more thorough
            if (
              shareError.name === 'AbortError' ||
              shareError.name === 'NotAllowedError' ||
              shareError.name === 'NotSupportedError' ||
              shareError.message?.includes('cancel') ||
              shareError.message?.includes('abort') ||
              shareError.message?.includes('user cancelled') ||
              shareError.message?.includes('user canceled') ||
              shareError.message?.includes('not supported')
            ) {
              console.log(
                'ℹ️ [DOWNLOAD ALL] User canceled or Share API not supported - exiting silently'
              );
              return; // Exit silently without showing success message
            }

            console.warn(
              '⚠️ [DOWNLOAD ALL] iOS Share API failed, falling back to FileSystem:',
              shareError
            );
            // Fall through to FileSystem method below
          }
        }
      }

      // For web, collect all files in a ZIP
      let zip: JSZip | null = null;
      if (!isMobile) {
        try {
          zip = new JSZip();
          console.log('📦 [DOWNLOAD ALL] ZIP mode enabled for web platform');
        } catch (zipError) {
          console.error(
            '❌ [DOWNLOAD ALL] Failed to create JSZip instance:',
            zipError
          );
          // Error logged to console - no alert needed
          return;
        }
      } else {
        console.log(
          '📱 [DOWNLOAD ALL] Mobile mode enabled - saving to FileSystem (fallback)'
        );
      }

      // Loop through all conversations
      for (const conversation of this.filteredConversations) {
        console.log(
          '🔍 [DOWNLOAD ALL] Processing conversation:',
          conversation.id,
          {
            inputType: conversation.inputType,
            hasPartnerAudio: this.hasPartnerAudio(conversation),
            hasOriginalAudio: this.hasOriginalAudio(conversation),
            hasTranslatedAudio: this.hasTranslatedAudio(conversation),
          }
        );

        // Download recorded audio (speech input)
        if (
          conversation.inputType === 'speech' &&
          this.hasPartnerAudio(conversation)
        ) {
          try {
            const audioUrl = this.getPartnerAudioUrl(conversation);
            const filename = `you-spoke-${this.getLanguageName(
              conversation.userLanguage
            )}-${conversation.id}.mp3`;
            const blob = await this.downloadAudioSilent(audioUrl, filename);
            if (blob && !isMobile && zip) {
              zip.file(filename, blob);
              console.log(
                '📁 [DOWNLOAD ALL] Added to ZIP:',
                filename,
                'Size:',
                blob.size,
                'bytes'
              );
            } else {
              console.log('⚠️ [DOWNLOAD ALL] Skipped ZIP addition:', filename, {
                hasBlob: !!blob,
                isMobile,
                hasZip: !!zip,
              });
            }
            downloadCount++;
            await this.delay(50);
          } catch (error) {
            console.error('Error downloading recorded audio:', error);
            errorCount++;
          }
        }

        // Download original audio (TTS for text input)
        if (
          conversation.inputType === 'text' &&
          this.hasOriginalAudio(conversation)
        ) {
          try {
            const audioUrl = this.getOriginalAudioUrl(conversation);
            const filename = `you-wrote-${this.getLanguageName(
              conversation.userLanguage
            )}-${conversation.id}.mp3`;
            const blob = await this.downloadAudioSilent(audioUrl, filename);
            if (blob && !isMobile && zip) {
              zip.file(filename, blob);
              console.log(
                '📁 [DOWNLOAD ALL] Added to ZIP:',
                filename,
                'Size:',
                blob.size,
                'bytes'
              );
            } else {
              console.log('⚠️ [DOWNLOAD ALL] Skipped ZIP addition:', filename, {
                hasBlob: !!blob,
                isMobile,
                hasZip: !!zip,
              });
            }
            downloadCount++;
            await this.delay(50);
          } catch (error) {
            console.error('Error downloading original audio:', error);
            errorCount++;
          }
        }

        // Download translated audio (always available)
        if (this.hasTranslatedAudio(conversation)) {
          try {
            const audioUrl = this.getTranslatedAudioUrl(conversation);
            const actionType =
              conversation.inputType === 'text' ? 'wrote' : 'hears';
            const filename = `partner-${actionType}-${this.getLanguageName(
              conversation.targetLanguage
            )}-${conversation.id}.mp3`;
            const blob = await this.downloadAudioSilent(audioUrl, filename);
            if (blob && !isMobile && zip) {
              zip.file(filename, blob);
              console.log(
                '📁 [DOWNLOAD ALL] Added to ZIP:',
                filename,
                'Size:',
                blob.size,
                'bytes'
              );
            } else {
              console.log('⚠️ [DOWNLOAD ALL] Skipped ZIP addition:', filename, {
                hasBlob: !!blob,
                isMobile,
                hasZip: !!zip,
              });
            }
            downloadCount++;
            await this.delay(50);
          } catch (error) {
            console.error('Error downloading translated audio:', error);
            errorCount++;
          }
        }
      }

      // For web, download the ZIP file
      if (!isMobile && zip) {
        try {
          console.log('📦 Creating ZIP file...');
          console.log('📦 ZIP contents:', Object.keys(zip.files));
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const zipUrl = URL.createObjectURL(zipBlob);
          const zipFilename = `conversation-audio-${
            new Date().toISOString().split('T')[0]
          }.zip`;

          console.log('📁 ZIP file details:');
          console.log('📁 Filename:', zipFilename);
          console.log(
            '📁 Size:',
            (zipBlob.size / 1024 / 1024).toFixed(2),
            'MB'
          );
          console.log('📁 Blob URL:', zipUrl);

          const link = document.createElement('a');
          link.href = zipUrl;
          link.download = zipFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(zipUrl);

          console.log('✅ ZIP file download initiated');
          console.log(
            "📁 File will be saved to your browser's default Downloads folder"
          );
          console.log('📁 Typical location: ~/Downloads/' + zipFilename);
        } catch (zipGenError) {
          console.error(
            '❌ [DOWNLOAD ALL] Failed to generate ZIP:',
            zipGenError
          );
          // Error logged to console - no alert needed
          return;
        }
      }

      // Log summary - no alerts for cleaner UX
      if (isMobile) {
        if (errorCount > 0) {
          console.log(
            `⚠️ Download completed with errors: ${downloadCount} succeeded, ${errorCount} failed`
          );
        } else {
          console.log(
            `✅ All ${downloadCount} audio files downloaded successfully`
          );
        }
      } else {
        // For web, just log to console - no alert to avoid interfering with Save As dialog
        console.log(
          `✅ Web download completed: ${downloadCount} files prepared for download`
        );
        if (errorCount > 0) {
          console.warn(`⚠️ ${errorCount} files failed to download`);
        }
      }
    } catch (error) {
      console.error('Error in download all:', error);
      // Error logged to console - no alert needed
    }
  }

  // Silent download method (no Share API prompts) for batch downloads
  // Returns the blob for web to add to ZIP
  private async downloadAudioSilent(
    audioUrl: string,
    filename: string
  ): Promise<Blob | null> {
    console.log('📥 [SILENT] Downloading:', filename);

    // Use Capacitor platform detection for consistency
    const { Capacitor } = await import('@capacitor/core');
    const isMobile = Capacitor.isNativePlatform();

    console.log('🔍 [SILENT] Platform detection:', {
      isNativePlatform: isMobile,
      platform: Capacitor.getPlatform(),
      userAgent: navigator.userAgent,
    });

    // Fetch the audio as a blob
    const response = await fetch(audioUrl);
    const blob = await response.blob();

    if (!isMobile) {
      // Desktop/Web: Return blob to be added to ZIP
      console.log(
        '✅ [SILENT] Blob fetched for web:',
        filename,
        'Size:',
        blob.size,
        'bytes'
      );
      return blob;
    }

    // Mobile: Use Capacitor FileSystem (skip Share API)
    const { Filesystem, Directory } = await import('@capacitor/filesystem');

    // Convert blob to base64
    const base64Data = await this.blobToBase64String(blob);

    // Save directly to Documents/Audio directory
    const result = await Filesystem.writeFile({
      path: `Audio/${filename}`,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true,
    });

    console.log('✅ [SILENT] File saved:', filename);
    console.log('📁 [SILENT] File URI:', result.uri);
    console.log('📁 [SILENT] Relative path: Documents/Audio/' + filename);

    // iOS-specific logging
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isIOS) {
      console.log('📱 [iOS] To access this file on your iPhone/iPad:');
      console.log('📱 [iOS] 1. Open the "Files" app');
      console.log('📱 [iOS] 2. Tap "Browse" at the bottom');
      console.log('📱 [iOS] 3. Scroll to "On My iPhone" (or "On My iPad")');
      console.log('📱 [iOS] 4. Find and tap "Kyrgz Translation"');
      console.log('📱 [iOS] 5. Open "Documents" folder');
      console.log('📱 [iOS] 6. Open "Audio" folder');
      console.log('📱 [iOS] 7. Your file:', filename);
      console.log('📱 [iOS] Full device path:', result.uri);
    }

    return null;
  }

  // Helper function to add delay
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  onClearAllHistory(): void {
    if (
      confirm(
        'Are you sure you want to clear all conversation history? This action cannot be undone.'
      )
    ) {
      this.conversationHistoryService.clearAllHistory();
      // Also emit an event to clear the main conversation arrays
      this.clearMainConversations.emit();
    }
  }

  onExportHistory(): void {
    const historyData = this.conversationHistoryService.exportHistory();
    const blob = new Blob([historyData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-history-${
      new Date().toISOString().split('T')[0]
    }.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const diffInHoursFloor = Math.floor(diffInHours);
      return `${diffInHoursFloor} hour${diffInHoursFloor !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 168) {
      // 7 days
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
  }

  formatDuration(duration?: number): string {
    if (!duration) return 'Unknown';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  getTotalConversations(): number {
    return this.conversationHistoryService.getTotalConversations();
  }

  getTotalDuration(): string {
    const totalSeconds = this.conversationHistoryService.getTotalDuration();
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  trackByConversationId(
    index: number,
    conversation: ConversationHistoryEntry
  ): string {
    return conversation.id;
  }

  getDisplayStyle(): string {
    return this.isOpen ? 'flex' : 'none';
  }

  private preventBodyScroll(): void {
    // Store current scroll position
    const scrollY = window.scrollY;

    // Apply styles to prevent scrolling
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    // Get the scroll position from the top style
    const scrollY = document.body.style.top;

    // Remove the fixed positioning styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restore scroll position
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }

  // Copy to clipboard with feedback
  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.showSuccessMessage('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy text:', error);
      // Fallback for older browsers or when clipboard API is not available
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showSuccessMessage('Copied to clipboard!');
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        alert('Failed to copy text');
      }
    }
  }

  // Show success message
  private showSuccessMessage(message: string): void {
    // Create a temporary success message element
    const messageEl = document.createElement('div');
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #10b981;
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    document.body.appendChild(messageEl);

    // Remove after 2 seconds
    setTimeout(() => {
      messageEl.remove();
    }, 2000);
  }

  // Check if audio is being generated for an entry
  isGeneratingAudio(entryId: string): boolean {
    return this.generatingAudioSet.has(entryId);
  }

  // Check if original audio is being generated for an entry
  isGeneratingOriginalAudio(entryId: string): boolean {
    return this.generatingOriginalAudioSet.has(entryId);
  }

  // Generate audio for a history entry that doesn't have it
  async generateAudioForEntry(
    conversation: ConversationHistoryEntry
  ): Promise<void> {
    if (!conversation.translatedText || !conversation.id) {
      return;
    }

    // Prevent duplicate generation
    if (this.generatingAudioSet.has(conversation.id)) {
      return;
    }

    this.generatingAudioSet.add(conversation.id);

    try {
      // Generate TTS audio for the translated text
      // Pass targetLanguage so backend can properly generate TTS for the target language
      const ttsResult = await this.translatorService.tts(
        conversation.translatedText,
        1.0, // Default speed
        'alloy', // Default voice
        conversation.targetLanguage // Pass target language for proper TTS generation
      );

      if (ttsResult && ttsResult.audioUrl && ttsResult.audioData) {
        // Update the conversation entry with the new audio
        const updatedConversation: ConversationHistoryEntry = {
          ...conversation,
          audioUrl: ttsResult.audioUrl,
          audioData: ttsResult.audioData,
        };

        // Update in the history service
        await this.conversationHistoryService.updateEntry(updatedConversation);

        this.toastService.showSuccess('Audio generated successfully!');

        // Refresh conversations to show the new audio
        this.filterConversations();
      } else {
        this.toastService.showError('Failed to generate audio');
      }
    } catch (error) {
      console.error('Error generating audio:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Error details:', {
        error,
        message: errorMessage,
        conversationId: conversation.id,
        targetLanguage: conversation.targetLanguage,
        translatedText: conversation.translatedText?.substring(0, 50),
      });
      this.toastService.showError(`Error generating audio: ${errorMessage}`);
    } finally {
      this.generatingAudioSet.delete(conversation.id);
    }
  }

  // Generate original audio for "You spoke" entries without recorded audio
  async generateOriginalAudioForEntry(
    conversation: ConversationHistoryEntry
  ): Promise<void> {
    if (!conversation.originalText || !conversation.id) {
      return;
    }

    // Prevent duplicate generation
    if (this.generatingOriginalAudioSet.has(conversation.id)) {
      return;
    }

    this.generatingOriginalAudioSet.add(conversation.id);

    try {
      // Generate TTS audio for the original text
      // Pass userLanguage so backend can properly generate TTS for the source language
      const ttsResult = await this.translatorService.tts(
        conversation.originalText,
        1.0, // Default speed
        'alloy', // Default voice
        conversation.userLanguage // Pass user language for proper TTS generation
      );

      if (ttsResult && ttsResult.audioUrl && ttsResult.audioData) {
        // Update the conversation entry with the new audio as recorded audio
        const updatedConversation: ConversationHistoryEntry = {
          ...conversation,
          recordedAudioUrl: ttsResult.audioUrl,
          recordedAudioData: ttsResult.audioData,
        };

        // Update in the history service
        await this.conversationHistoryService.updateEntry(updatedConversation);

        this.toastService.showSuccess('Original audio generated successfully!');

        // Refresh conversations to show the new audio
        this.filterConversations();
      } else {
        this.toastService.showError('Failed to generate original audio');
      }
    } catch (error) {
      console.error('Error generating original audio:', error);
      this.toastService.showError('Error generating original audio');
    } finally {
      this.generatingOriginalAudioSet.delete(conversation.id);
    }
  }
}
